import { Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { map, Observable } from 'rxjs';
import { dualLanguageCustomValidators } from '../../../../../form-builder/services/form/custom-validators';
import { FormValidation } from '../../../../../ui-components/enums/form-validation';
import { AttachmentTypeEnum } from '../../../../../ui-components/lib/upload-files/attachment-type.enum';
import { WorkflowKind } from '../workflow.model';

export interface Localized {
  en: string;
  ar: string;
}

export enum CustomWorkflowStatus {
  Draft = 'draft',
  Published = 'published',
}

export enum FieldAccess {
  View = 'view',
  Edit = 'edit',
  Hidden = 'hidden',
}

export enum PublishErrorCode {
  NoFields = 'no_fields',
  NoSteps = 'no_steps',
  StepWithoutApprover = 'step_without_approver',
  NobodyCanSubmit = 'nobody_can_submit',
}

/** Which editor the canvas drawer shows. */
export enum DrawerKind {
  Form = 'form',
  Step = 'step',
  Settings = 'settings',
}

export interface CustomWorkflowListItem {
  id: number;
  name: string;
  category: { id: number; name: string } | null;
  icon: { url?: string } | null;
  status: CustomWorkflowStatus;
  is_active: boolean;
  requests_count: number;
  members_count: number;
}

export interface CustomWorkflowFieldOption {
  value: string;
  label: Localized;
}

/** Builder settings of a field, nested under `metadata`. */
export interface CustomWorkflowFieldMetadata {
  mandatory: boolean;
  hint: Localized | null;
  placeholder: Localized | null;
  options: CustomWorkflowFieldOption[];
  /** Comma-separated, e.g. `pdf,png`. */
  extensions: string;
  /** In KB. */
  max_file_size: number;
  attachment_type: AttachmentTypeEnum;
  lookup_model_config_id: number | null;
}

/** Display values come in the request language; `translations` carries every language for the edit forms. */
export interface CustomWorkflowField {
  id: number;
  key: string;
  type: string;
  label: string;
  order: number;
  section_id: number;
  translations: { label: Localized };
  metadata: CustomWorkflowFieldMetadata;
}

export interface CustomWorkflowSection {
  id: number;
  key: string;
  label: string;
  order: number;
  translations: { label: Localized };
  fields: CustomWorkflowField[];
}

export interface CustomWorkflowAssignee {
  type: string;
  assignable_type: string | null;
  data: { id?: number | string; name?: string } | null;
}

export interface CustomWorkflowStep {
  id: number;
  name: string;
  position_name: string;
  position: number;
  is_initiation: boolean;
  completion_action: string | null;
  sla: number;
  editable_fields: string[];
  hidden_fields: string[];
  translations: {
    name?: Partial<Localized>;
    position_name?: Partial<Localized>;
  };
  assignees: CustomWorkflowAssignee[];
  /** Active reflections bound to this step; absent on older responses. */
  reflections_count?: number;
}

export interface CustomWorkflowRequirement {
  id: number;
  value: string;
  translations: { value?: Partial<Localized> };
}

export interface CustomWorkflow {
  id: number;
  name: string;
  description: string | null;
  category_id: number;
  can_be_sent_back_to_first_position: boolean;
  has_step_actions: boolean;
  status: CustomWorkflowStatus;
  is_active: boolean;
  kind: WorkflowKind;
  locked: boolean;
  requests_count: number;
  members_count: number;
  translations: { name?: Partial<Localized>; description?: Partial<Localized> };
  category: { id: number; name: string } | null;
  icon: { id?: number; url?: string } | null;
  sections: CustomWorkflowSection[];
  steps: CustomWorkflowStep[];
  requirements: CustomWorkflowRequirement[];
}

export interface CustomWorkflowFieldType {
  value: string;
  label: string;
  has_options: boolean;
  has_placeholder: boolean;
  is_media: boolean;
  is_lookup: boolean;
}

/** What the media upload API accepts for an attachment type; `max_file_size` is in KB. */
export interface CustomWorkflowAttachmentType {
  value: AttachmentTypeEnum;
  label: string;
  extensions: string[];
  max_file_size: number;
}

/** A model-based lookup (Vendor, City, Department, …); `key` is the model's morph key. */
export interface CustomWorkflowLookupModel {
  id: number;
  key: string;
  name: string;
  translations: null;
  type: null;
}

/** A Variable-based lookup (Action reason, Sector, Tax, …), with translations and a single/multi type. */
export interface CustomWorkflowLookupVariable {
  id: number;
  key: string;
  name: string;
  translations: { name: Localized };
  type: { value: 'single' | 'multi'; label: string };
}

export interface CustomWorkflowFieldOptions {
  types: CustomWorkflowFieldType[];
  lookup_models: CustomWorkflowLookupModel[];
  lookup_variables: CustomWorkflowLookupVariable[];
  attachment_types?: CustomWorkflowAttachmentType[];
}

export interface PublishError {
  code: PublishErrorCode;
  message: string;
  step_id?: number;
}

/** Body the error interceptor rethrows (it strips HttpErrorResponse down to the JSON body). */
export interface CustomWorkflowApiError {
  message?: string;
  errors?: Record<string, string[]>;
  locked?: boolean;
  publish_errors?: PublishError[];
}

export function localized(
  value: Partial<Localized> | null | undefined,
  lang: string
): string {
  if (!value) return '';
  return (lang === 'ar' ? value.ar : value.en) || value.en || value.ar || '';
}

/** Collapses a blank/all-null {en, ar} value (e.g. an unset hint/placeholder) to null. */
export function localizedOrNull(
  value: Partial<Localized> | null | undefined
): Localized | null {
  if (!value) return null;
  return value.en || value.ar ? (value as Localized) : null;
}

/**
 * An {en, ar} group for lib-dual-lang-input, built like the contract form's name: the controls only cap
 * the length, the group itself requires at least one language. Save paths run
 * FormService.syncDualLangControls() first so the blank language is filled from the other one.
 */
export function localizedGroup(
  value: Partial<Localized> = {},
  { required = true, maxLength = FormValidation.maxLength } = {}
) {
  return new FormGroup(
    {
      en: new FormControl(value.en ?? '', {
        nonNullable: true,
        validators: Validators.maxLength(maxLength),
      }),
      ar: new FormControl(value.ar ?? '', {
        nonNullable: true,
        validators: Validators.maxLength(maxLength),
      }),
    },
    {
      validators: required
        ? dualLanguageCustomValidators.atLeastOneLangRequired
        : null,
    }
  );
}

/** A form's raw value as a signal, re-read on every value change, so `dirty` can be a computed. Needs an injection context. */
export function rawValueSignal<T>(form: {
  valueChanges: Observable<unknown>;
  getRawValue(): T;
}): Signal<T> {
  return toSignal(form.valueChanges.pipe(map(() => form.getRawValue())), {
    initialValue: form.getRawValue(),
  });
}

/** Lower-case snake key from a label; empty for labels with no latin letters or digits. */
export function slugifyKey(label: string): string {
  const key = label
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  // Keys must start with a letter (backend rule ^[a-z][a-z0-9_]*$).
  return /^[0-9]/.test(key) ? `f_${key}` : key;
}
